/**
 * Purpose: Tracks whether the app/page is currently visible to the user
 *
 * How it works:
 * 1. Uses document.hidden as the primary visibility signal
 * 2. Listens to visibilitychange, pageshow/pagehide, focus/blur events
 * 3. Caches the result and only logs on state changes
 *
 * Key features:
 * - No React dependency — works from plain TS modules
 * - Uses the Page Visibility API (document.hidden / visibilitychange)
 * - pageshow/pagehide listeners for iOS Safari reliability
 *   (iOS doesn't reliably fire focus/blur when switching apps or locking)
 * - document.hidden is the primary signal; hasFocus() is supplementary
 *   (iOS hasFocus() can return false even when the page is in foreground)
 * - Event listeners are registered once at module load and intentionally
 *   never removed — they must persist for the entire application lifetime
 */

import { createLogger } from "./debug";

const log = createLogger("Visibility");

let cachedAppVisible = true;

/**
 * Compute visibility using document.hidden as the primary signal.
 * hasFocus() is only used on desktop to detect "tab visible but window
 * not focused" — on iOS it is unreliable so we don't require it.
 */
function computeAppVisible(): boolean {
  if (typeof document === "undefined") {
    return true;
  }

  // document.hidden is the reliable cross-platform signal
  return !document.hidden;
}

if (
  typeof document !== "undefined" &&
  typeof document.addEventListener === "function"
) {
  cachedAppVisible = computeAppVisible();

  const updateVisibility = () => {
    const previousVisibility = cachedAppVisible;
    cachedAppVisible = computeAppVisible();

    if (previousVisibility !== cachedAppVisible) {
      log.info("App visibility changed", {
        visibilityState: document.visibilityState,
        appVisible: cachedAppVisible,
      });
    }
  };

  // Standard visibility change — works on most browsers
  document.addEventListener("visibilitychange", updateVisibility);

  // iOS Safari fires pageshow/pagehide more reliably than focus/blur
  // when the user switches apps, locks the screen, or uses the app switcher
  globalThis.addEventListener("pageshow", updateVisibility);
  globalThis.addEventListener("pagehide", updateVisibility);

  // Desktop: detect window losing/gaining focus within the same tab
  globalThis.addEventListener("focus", updateVisibility);
  globalThis.addEventListener("blur", updateVisibility);
}

/**
 * Returns true if the page is currently visible to the user.
 * Falls back to true if the Page Visibility API is not available.
 */
export function isAppVisible(): boolean {
  if (typeof document === "undefined") {
    return true;
  }

  return cachedAppVisible;
}
